"use client";

import {
  FC,
  ReactNode,
  useEffect,
  useState,
} from "react";
import TextTitle from "apps/website/components/base/Text/TextTitle/TextTitle";
import TextSubtitle from "apps/website/components/base/Text/TextSubtitle/TextSubtitle";
import TextBody from "apps/website/components/base/Text/TextBody/TextBody";
import Button from "apps/website/components/base/Button/Button";
import { CTAButton } from "apps/website/components/base/CTAButton/CTAButton";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";
import Image, { IImageProps } from "apps/website/components/base/Image/Image";
import useFocusTrap from "apps/website/hooks/useFocusTrap";
import useModal from "apps/website/hooks/useModal";
import Container from "apps/website/components/layout/Container/Container";
import Column from "apps/website/components/layout/Column/Column";
import Icon from "apps/website/components/base/Icon/Icon";
import Tag from "apps/website/components/base/Tag/Tag";

import { DisplayState } from "@/constants/state";

import Modal from "../Modal";

export type NibblesPouchesPurchaseState = "purchased" | "processing" | "failure" | "none";

export interface INibblesPouchesUpsellModalProps {
  id: string;
  isOpen: boolean;
  onClose(): void;
  onConfirm(): void;
  onPickOwn(): void;
  modalTitleId: string;
  modalDescriptionId: string;
  theme?: Theme;
  title?: string;
  subheading?: string;
  description?: string;
  image?: IImageProps;
  items?: ReactNode[];
  priceBeforeDiscount?: number;
  priceAfterDiscount?: number;
  footerText?: string;
  addToOrderButtonText?: string;
  buttonProcessingText?: string;
  pickOwnButtonText?: string;
  result?: NibblesPouchesPurchaseState;
  successText?: string;
  successImage?: IImageProps;
  cardTag?: string;
}

const DEFAULT_ITEMS: ReactNode[] = [
  <>1 x 15g <strong className="font-bold">salmon</strong> fillet Nibbles</>,
  <>1 x 15g <strong className="font-bold">tuna</strong> fillet Nibbles</>,
  <>1 x 15g <strong className="font-bold">chicken</strong> breast Nibbles</>,
];

const NibblesPouchesUpsellModal: FC<INibblesPouchesUpsellModalProps> = ({
  id,
  isOpen,
  onClose,
  onConfirm,
  onPickOwn,
  theme = "light-grey",
  title = "Get 50% off treats",
  subheading = "NIBBLES TRIO BUNDLE - 100% MEAT + FISH",
  description = "A bundle of our irresistible freeze-dried 100% meat and fish treats. No sugar, no nasties, no fuss.",
  image = {
    src: "/images/nibbles-trio-over-table.webp",
    width: 4991,
    height: 2870,
  },
  items = DEFAULT_ITEMS,
  priceBeforeDiscount = 5.97,
  priceAfterDiscount = 2.99,
  footerText = "Add these to your ongoing plan. Change or remove at any time from your account.",
  addToOrderButtonText = "Add to box",
  buttonProcessingText = "Adding...",
  pickOwnButtonText = "I want to pick my own",
  result,
  successText = "Added to your ongoing deliveries.",
  successImage = {
    src: "https://a.storyblok.com/f/176726/150x150/8f369a4b79/katkin_thescratchingpost_january_illus-1.svg",
    width: 500,
    height: 300,
  },
  cardTag = "50% off",
}) => {
  const { modalId, modalTitleId, modalDescriptionId, modalButtonId, modalIsOpen, setModalIsOpen } = useModal(isOpen);
  const { initFocusTrap, destroyFocusTrap } = useFocusTrap();
  const [ buttonState, setButtonState ] = useState(DisplayState.READY);

  useEffect(() => {
    if (result === "purchased") {
      setButtonState(DisplayState.COMPLETE);
    } else if (result === "processing") {
      setButtonState(DisplayState.PROCESSING);
    } else if (result === "failure") {
      setButtonState(DisplayState.ERROR);
    }
  }, [ result ]);

  useEffect(() => {
    if (modalIsOpen) {
      initFocusTrap(modalId);
    } else {
      destroyFocusTrap(modalButtonId);
      if (onClose) {
        onClose();
      }
    }
  }, [ destroyFocusTrap, initFocusTrap, modalButtonId, modalId, modalIsOpen, onClose ]);

  return (
    <Modal
      id={id}
      component={NibblesPouchesUpsellModal.name}
      isOpen={isOpen}
      onClose={() => { setModalIsOpen(false); }}
      modalTitleId={modalTitleId}
      modalDescriptionId={modalDescriptionId}
      hideCloseButton={false}
      size="sm"
      theme={theme}
    >
      <Container
        className="flex flex-col py-5 px-2 items-center justify-center align-middle"
      >
        { result !== "purchased" ? (
          <>
            <TextSubtitle data-title size={2} align="center">
              { title }
            </TextSubtitle>
            <Spacer size="sm" />
            <div className="w-full bg-white">
              <div className="relative">
                <Image image={image} alt="Nibbles Trio Bundle"></Image>
                { cardTag && (
                  <Tag theme="pink" className="absolute top-2 left-2 z-40" whitespace="preWrap">{ cardTag }</Tag>
                ) }
              </div>
              <div className="flex flex-col items-center p-4">
                <TextSubtitle align="center" size={3}>
                  { subheading }
                </TextSubtitle>
                <Spacer size="sm" />
                <TextBody className="!mb-0" align="center" size={3}>
                  { description }
                </TextBody>
                <Spacer size="sm" />
                <ul className="list-none text-center" data-testid="nibbles-pouches-items">
                  { items.map((item, index) => (
                    <li key={index}>
                      <TextBody tag="span" align="center" size={3}>{ item }</TextBody>
                    </li>
                  )) }
                </ul>
                <Spacer size="sm" />
                <div className="flex gap-2 items-baseline justify-center">
                  <TextBody
                    tag="span"
                    align="center"
                    size={3}
                    className="line-through"
                  >
                    { `£${priceBeforeDiscount.toFixed(2)}` }
                  </TextBody>
                  <TextSubtitle align="center" size={3}>
                    { `£${priceAfterDiscount.toFixed(2)}` }
                  </TextSubtitle>
                </div>
                <Spacer size="sm" />
                <TextBody align="center" size={3}>
                  { footerText }
                </TextBody>
              </div>
            </div>
            <Spacer size="lg" />
            <Container dataTestId="nibbles-pouches-ctas" className="flex flex-col sm:flex-row gap-2 items-center justify-center w-full">
              <Button
                onClick={onConfirm}
                size="default"
                state={buttonState}
              >
                { buttonState === DisplayState.PROCESSING ? buttonProcessingText : buttonState === DisplayState.ERROR ? "Something went wrong" : addToOrderButtonText }
              </Button>
              <CTAButton
                text={pickOwnButtonText}
                design="underline"
                onClick={onPickOwn}
              />
            </Container>
            <Spacer size="lg" />
          </>
        ) : (
          <Column className="min-h-96 items-center justify-center text-center px-4">
            { successImage && (
              <div className="w-1/2">
                <Image image={successImage} alt="Nibbles Trio Bundle"></Image>
              </div>
            ) }
            <div className={`px-2 lg:px-4 h-11 flex justify-center items-center ${themeRootClassMap.green}`} data-theme="green" data-testid="alwaysInBox">
              <Icon icon="tickHeavy" size="small" />
              <TextTitle tag="span" size={3}>
                In your box
              </TextTitle>
            </div>
            <Spacer size="lg" />
            <TextSubtitle align="center" size={2}>
              { successText }
            </TextSubtitle>
            <Spacer size="lg" />
            <Spacer size="lg" />
          </Column>
        ) }
      </Container>
    </Modal>
  );
};

export default NibblesPouchesUpsellModal;
